import { consola } from 'consola'
/**
 * Утилиты для работы с программой лояльности
 */

/**
 * Интерфейс информации о лояльности
 */
export interface LoyaltyInfo {
  points: number
  cashbackLevel: string | number
}


/**
 * Константы для работы с лояльностью
 */
export const LOYALTY_CONSTANTS = {
  /** Фолбэк, пока настройки заведения не загружены. Реальный лимит — `loyalty_max_percent_redeem`. */
  MAX_POINTS_USAGE_PERCENT: 0.5,
  MIN_POINTS_TO_USE: 1, // Минимум баллов для списания
} as const

/**
 * Доля заказа, которую разрешено оплатить баллами.
 * Лимит настраивается в админке на заведение (у одного 50%, у другого 30%) —
 * захардкоженные 50% давали корзине предложить больше, чем примет бэкенд.
 */
function resolveMaxPercent(maxPercent?: number): number {
  return typeof maxPercent === 'number' && maxPercent > 0 && maxPercent <= 1
    ? maxPercent
    : LOYALTY_CONSTANTS.MAX_POINTS_USAGE_PERCENT
}

/**
 * Вычисляет максимальное количество баллов, которое можно использовать
 * @param cartTotal - общая сумма корзины
 * @param availablePoints - доступные баллы
 * @param maxPercent - доля 0..1 из настроек заведения (по умолчанию — фолбэк 0.5)
 * @returns максимальное количество баллов для использования
 */
export function calculateMaxPointsToUse(
  cartTotal: number,
  availablePoints: number,
  maxPercent?: number,
): number {
  const maxByPercent = Math.floor(cartTotal * resolveMaxPercent(maxPercent))
  return Math.min(availablePoints, maxByPercent)
}

/**
 * Валидирует количество баллов для использования
 * @param pointsToUse - количество баллов для использования
 * @param cartTotal - общая сумма корзины
 * @param availablePoints - доступные баллы
 * @returns true если количество валидно
 */
export function validatePointsToUse(
  pointsToUse: number,
  cartTotal: number,
  availablePoints: number,
  maxPercent?: number,
): boolean {
  if (pointsToUse < 0) return false
  if (pointsToUse > availablePoints) return false

  const maxAllowed = calculateMaxPointsToUse(cartTotal, availablePoints, maxPercent)
  return pointsToUse <= maxAllowed
}

/**
 * Вычисляет кешбэк за заказ
 * @param finalAmount - итоговая сумма заказа
 * @param cashbackLevel - уровень кешбэка (в процентах)
 * @returns количество баллов кешбэка
 */
export function calculateCashback(finalAmount: number, cashbackLevel: string | number): number {
  // Обрабатываем строки типа "3%" или числа
  let rate: number
  if (typeof cashbackLevel === 'string') {
    // Убираем символ % и конвертируем в число
    const cleanValue = cashbackLevel.replace('%', '').trim()
    rate = Number(cleanValue) / 100
  } else {
    rate = Number(cashbackLevel)
  }
  
  // Проверяем валидность
  if (Number.isNaN(rate) || Number.isNaN(finalAmount)) {
    consola.warn('Invalid values for cashback calculation:', { finalAmount, cashbackLevel, rate })
    return 0
  }
  
  // Используем Math.round для правильного округления
  return Math.round(finalAmount * rate)
}

/**
 * Нормализует количество баллов к валидному диапазону
 * @param pointsToUse - исходное количество баллов
 * @param cartTotal - общая сумма корзины
 * @param availablePoints - доступные баллы
 * @returns нормализованное количество баллов
 */
export function normalizePointsToUse(
  pointsToUse: number,
  cartTotal: number,
  availablePoints: number,
  maxPercent?: number,
): number {
  if (pointsToUse < 0) return 0

  const maxAllowed = calculateMaxPointsToUse(cartTotal, availablePoints, maxPercent)
  return Math.min(pointsToUse, maxAllowed)
}

/**
 * Вычисляет сумму после списания баллов
 * @param originalAmount - исходная сумма
 * @param pointsToUse - количество списываемых баллов
 * @returns сумма после списания баллов
 */
export function calculateAmountAfterPoints(originalAmount: number, pointsToUse: number): number {
  return Math.max(0, originalAmount - pointsToUse)
}

/**
 * Форматирует информацию о лояльности для отображения
 * @param loyaltyInfo - информация о лояльности
 * @param cartTotal - сумма корзины
 * @param pointsToUse - количество используемых баллов
 * @returns объект с форматированной информацией
 */
export function formatLoyaltyInfo(
  loyaltyInfo: LoyaltyInfo,
  cartTotal: number,
  pointsToUse: number = 0,
) {
  const maxPointsToUse = calculateMaxPointsToUse(cartTotal, loyaltyInfo.points)
  
  // Правильно обрабатываем cashbackLevel для расчета ставки
  let cashbackRate: number
  if (typeof loyaltyInfo.cashbackLevel === 'string') {
    const cleanValue = loyaltyInfo.cashbackLevel.replace('%', '').trim()
    cashbackRate = Number(cleanValue) / 100
  } else {
    cashbackRate = Number(loyaltyInfo.cashbackLevel)
  }

  const amountAfterPoints = calculateAmountAfterPoints(cartTotal, pointsToUse)
  const cashbackAmount = calculateCashback(amountAfterPoints, loyaltyInfo.cashbackLevel)
  
  return {
    availablePoints: loyaltyInfo.points,
    maxPointsToUse,
    cashbackRate,
    cashbackAmount,
    amountAfterPoints,
    isPointsUsageValid: validatePointsToUse(pointsToUse, cartTotal, loyaltyInfo.points),
  }
}

/**
 * Создает обработчик изменения количества баллов
 * @param cartTotal - сумма корзины
 * @param availablePoints - доступные баллы
 * @returns функция-обработчик
 */
export function createPointsInputHandler(cartTotal: number, availablePoints: number) {
  return (inputValue: number): number => {
    return normalizePointsToUse(inputValue, cartTotal, availablePoints)
  }
}

/**
 * Получает максимальное количество баллов одним кликом
 * @param cartTotal - сумма корзины
 * @param availablePoints - доступные баллы
 * @returns максимальное количество баллов для быстрого выбора
 */
export function getMaxPointsForQuickSelect(cartTotal: number, availablePoints: number): number {
  return calculateMaxPointsToUse(cartTotal, availablePoints)
}

/**
 * Интерфейс для уровня лояльности
 */
export interface LoyaltyLevel {
  id: string;
  percent: string;
  min_spent: number;
}

/**
 * Интерфейс для информации о прогрессе лояльности
 */
export interface LoyaltyProgress {
  currentLevel: LoyaltyLevel | null;
  nextLevel: LoyaltyLevel | null;
  spentAmount: number;
  remainingToNext: number;
  progressPercentage: number;
  isMaxLevel: boolean;
}

/**
 * Находит текущий уровень лояльности пользователя
 * @param totalSpent - общая сумма потраченных средств
 * @param levels - массив уровней лояльности, отсортированный по min_spent
 * @returns текущий уровень или null
 */
export function findCurrentLevel(totalSpent: number, levels: LoyaltyLevel[]): LoyaltyLevel | null {
  // Сортируем уровни по возрастанию min_spent
  const sortedLevels = [...levels].sort((a, b) => a.min_spent - b.min_spent);
  
  // Находим последний уровень, который пользователь достиг
  let currentLevel: LoyaltyLevel | null = null;
  
  for (const level of sortedLevels) {
    if (totalSpent >= level.min_spent) {
      currentLevel = level;
    } else {
      break;
    }
  }
  
  return currentLevel;
}

/**
 * Находит следующий уровень лояльности
 * @param totalSpent - общая сумма потраченных средств
 * @param levels - массив уровней лояльности, отсортированный по min_spent
 * @returns следующий уровень или null если достигнут максимальный
 */
export function findNextLevel(totalSpent: number, levels: LoyaltyLevel[]): LoyaltyLevel | null {
  // Сортируем уровни по возрастанию min_spent
  const sortedLevels = [...levels].sort((a, b) => a.min_spent - b.min_spent);
  
  // Находим первый уровень, который пользователь еще не достиг
  for (const level of sortedLevels) {
    if (totalSpent < level.min_spent) {
      return level;
    }
  }
  
  return null; // Пользователь достиг максимального уровня
}

/**
 * Рассчитывает прогресс лояльности пользователя
 * @param totalSpent - общая сумма потраченных средств
 * @param levels - массив уровней лояльности
 * @returns информация о прогрессе лояльности
 */
export function calculateLoyaltyProgress(totalSpent: number, levels: LoyaltyLevel[]): LoyaltyProgress {
  const currentLevel = findCurrentLevel(totalSpent, levels);
  const nextLevel = findNextLevel(totalSpent, levels);
  
  let remainingToNext = 0;
  let progressPercentage = 0;
  let isMaxLevel = false;
  
  if (nextLevel) {
    // Есть следующий уровень
    remainingToNext = nextLevel.min_spent - totalSpent;
    
    // Рассчитываем прогресс от текущего уровня к следующему
    if (currentLevel) {
      const currentLevelSpent = currentLevel.min_spent;
      const nextLevelSpent = nextLevel.min_spent;
      const spentInCurrentLevel = totalSpent - currentLevelSpent;
      const totalNeededForNext = nextLevelSpent - currentLevelSpent;
      
      progressPercentage = totalNeededForNext > 0 
        ? Math.min(100, (spentInCurrentLevel / totalNeededForNext) * 100)
        : 100;
    } else {
      // Пользователь еще не достиг первого уровня
      progressPercentage = (totalSpent / nextLevel.min_spent) * 100;
    }
  } else {
    // Достигнут максимальный уровень
    isMaxLevel = true;
    progressPercentage = 100;
  }
  
  return {
    currentLevel,
    nextLevel,
    spentAmount: totalSpent,
    remainingToNext,
    progressPercentage: Math.round(progressPercentage),
    isMaxLevel,
  };
}